import { readJson, writeJson } from "./storage";
import type { ApiResult } from "./http";

type CacheEntry<T> = { savedAt: number; value: T };

const inflight = new Map<string, Promise<{ cached: CacheEntry<any> | null; result: ApiResult<any> }>>();

export function loadCache<T>(key: string): CacheEntry<T> | null {
  return readJson<CacheEntry<T> | null>(key, null);
}

export function saveCache<T>(key: string, value: T): void {
  writeJson(key, { savedAt: Date.now(), value } satisfies CacheEntry<T>);
}

export function isFresh(savedAt: number, maxAgeMs: number): boolean {
  return Date.now() - savedAt <= maxAgeMs;
}

export async function fetchWithCache<T>(
  key: string,
  fetcher: () => Promise<ApiResult<T>>,
  opts: { maxAgeMs: number }
): Promise<{ cached: CacheEntry<T> | null; result: ApiResult<T> }> {
  const cached = loadCache<T>(key);
  if (cached && isFresh(cached.savedAt, opts.maxAgeMs)) {
    return { cached, result: { ok: true, data: cached.value } };
  }

  const existing = inflight.get(key) as Promise<{ cached: CacheEntry<T> | null; result: ApiResult<T> }> | undefined;
  if (existing) return existing;

  const run = (async () => {
    const res = await fetcher();
    if (res.ok) saveCache(key, res.data);
    return { cached, result: res };
  })();

  inflight.set(key, run as Promise<{ cached: CacheEntry<any> | null; result: ApiResult<any> }>);

  try {
    return await run;
  } finally {
    inflight.delete(key);
  }
}
